import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Flame, Heart } from "lucide-react";
import { fadeUp, stagger } from "@/lib/motion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * The Glowgetter Wall — the week's most-loved reader comments from every
 * story on the site, ranked by hearts. Fresh picks weekly, with an all-time
 * backfill when the week is quiet. Each card links back to its story.
 */
export default function GlowgetterWall() {
  const { data: comments } = useQuery({
    queryKey: ["glowgetter-wall"],
    queryFn: async () => {
      try {
        const { data } = await axios.get(`${API}/glowgetter-wall`);
        return data;
      } catch {
        return null;
      }
    },
    initialData: null,
    initialDataUpdatedAt: 0,
    refetchInterval: 5 * 60 * 1000,
  });

  const wall = comments || [];

  return (
    <section
      id="glowgetter-wall"
      data-testid="glowgetter-wall"
      className="scroll-mt-24 py-24 lg:py-32"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.15 }}
        >
          <motion.div variants={fadeUp} className="max-w-2xl">
            <p className="mb-4 inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-[0.24em] text-cherry">
              <Flame className="h-3.5 w-3.5" /> The Glowgetter Wall
            </p>
            <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">
              The loudest Glowgetters{" "}
              <span className="font-garamond italic text-cherry">this week.</span>
            </h2>
            <p className="mt-5 text-base leading-relaxed text-white/65 sm:text-lg">
              Real comments from real readers. Drop a take under any story,
              collect hearts, and the best of the week lands right here.
            </p>
          </motion.div>

          {wall.length === 0 ? (
            <motion.p
              variants={fadeUp}
              data-testid="glowgetter-wall-empty"
              className="mt-10 rounded-3xl border border-dashed border-white/15 bg-panel/60 p-10 text-center text-base leading-relaxed text-white/55"
            >
              The wall fills up with the week's most-loved comments. Every
              story has a group chat at the bottom —{" "}
              <span className="text-cherry">be the first Glowgetter on the board.</span>
            </motion.p>
          ) : (
            <div className="mt-10 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
              {wall.map((c, i) => (
                <motion.div
                  key={c.id}
                  variants={fadeUp}
                  data-testid={`glowgetter-wall-card-${i + 1}`}
                  className="flex h-full flex-col justify-between rounded-3xl border border-white/10 bg-panel p-7 transition-colors duration-300 hover:border-cherry/50"
                >
                  <p className="text-sm leading-relaxed text-white/80 sm:text-base">
                    "{c.body}"
                  </p>
                  <div className="mt-6 flex items-center justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-3">
                      <span
                        aria-hidden="true"
                        className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-full bg-cherry/15 text-sm font-bold text-cherry"
                      >
                        {c.avatar_url ? (
                          <img
                            src={c.avatar_url}
                            alt=""
                            className="h-full w-full object-cover"
                          />
                        ) : (
                          (c.name || "?").trim().charAt(0).toUpperCase()
                        )}
                      </span>
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-bold text-white">
                          {c.name}
                        </span>
                        <Link
                          data-testid={`glowgetter-wall-link-${i + 1}`}
                          to={c.article_path}
                          className="block truncate text-[11px] font-semibold uppercase tracking-[0.14em] text-white/45 transition-colors hover:text-cherry"
                        >
                          on {c.article_label}
                        </Link>
                      </span>
                    </div>
                    <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-cherry/35 bg-cherry/10 px-3 py-1 text-[11px] font-bold text-cherry">
                      <Heart className="h-3 w-3 fill-current" /> {c.likes}
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
}
